import { createContext, useContext, useEffect, useState } from 'react';
import { getTradebooksJson } from '../../../services/PacketService';
import { levenshteinDistance } from '../../../utils/string';

const LoadedBooksContext = createContext({});

export const useDiffitBooks = () => useContext(LoadedBooksContext);

export const DiffitBooksProvider = ({ children }) => {
  const [diffitBooks, setDiffitBooks] = useState([]);
  useEffect(() => {
    const loadBooks = async () => {
      try {
        const response = await getTradebooksJson();
        const books = Object.values(response.data).filter((book) => !book.hidden);
        const sortedBooks = books.sort((a, b) => a.title.localeCompare(b.title));
        setDiffitBooks(sortedBooks);
      } catch (e) {
        console.error(e);
      }
    };
    loadBooks();
  }, []);

  const findPossibleBookMatchForTopic = (topic) => {
    // TODO: move getPossibleMatchingBooks to this file once Diffit 1.0 is deprecated
    const possibleMatchingBooks = getPossibleMatchingBooks(topic, diffitBooks);
    if (possibleMatchingBooks.length > 0) {
      return possibleMatchingBooks[0];
    }
    return null;
  };

  return (
    <LoadedBooksContext.Provider value={{ diffitBooks, findPossibleBookMatchForTopic }}>
      {children}
    </LoadedBooksContext.Provider>
  );
};

export const getListOfStartChapters = (chapters, endChapterIdx) => {
  if (!chapters) {
    return [];
  }
  if (typeof chapters[0] === 'object') {
    chapters = chapters.map((c) => c.title);
  }
  // Index starts at 1 because we can't support a value of 0 in the Listbox
  chapters = chapters.map((c, idx) => ({
    name: c,
    value: idx + 1,
  }));
  if (endChapterIdx === -1) {
    return chapters;
  }
  return chapters.slice(0, endChapterIdx + 1);
};

export const getListOfEndChapters = (chapters, startChapterIdx) => {
  if (!chapters) {
    return [];
  }
  if (typeof chapters[0] === 'object') {
    chapters = chapters.map((c) => c.title);
  }
  // Index starts at 1 because we can't support a value of 0 in the Listbox
  chapters = chapters.map((c, idx) => ({
    name: c,
    value: idx + 1,
  }));
  if (startChapterIdx === -1) {
    return chapters;
  }
  return chapters.slice(startChapterIdx);
};

export const cleanBookTitle = (title) => {
  return title.replace('_', ': ');
};

export const makeBookId = (book) => {
  if (!book) {
    return '';
  }
  return book.title + '--diffit--' + book.author;
};

export const getChapterFromIdx = (selectedBook, idx) => {
  if (selectedBook === undefined || idx <= -1) {
    return '';
  }
  if (typeof selectedBook.chapters[idx] === 'object') {
    return selectedBook.chapters[idx]?.title || '';
  }
  return selectedBook.chapters[idx] || '';
};

export const getPossibleMatchingBooks = (topic, books) => {
  // The threshold that we will consider a book a 'match'
  // Longer inputs need a lower threshold, because there are more surrounding words to consider
  const SCORE_TO_MATCH_ON = topic.length < 7 ? 100 : topic.length < 15 ? 90 : 70;

  // add some weights for if "chapter" or an author we know about are in the input;
  // if either of these exist, it's more likely that the user is searching for a book
  const WEIGHT_FOR_AUTHOR = 30;
  const WEIGHT_FOR_CHAPTER = 30;

  const topicCleaned = topic
    .toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .replace('book', '')
    .replace('novel', '')
    .trim();

  const edgeCaseMatches = getEdgeCaseMatches(topicCleaned, books);
  if (edgeCaseMatches) {
    return edgeCaseMatches;
  }

  const matches = [];
  for (const book of books) {
    let title = book.title
      .toLowerCase()
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .trim();
    if (title.includes('_')) {
      // some books are formatted as "Author_Title", so we need to extract the title
      title = title.split('_')[1];
    }

    // if the exact title is in our input, we should boost the likelihood that this is a match
    // longer matches get a higher boost (this helps filter out common words that are
    // also the name of a book, e.g. "night"). Teachers often drop a title's leading
    // article ("Catcher in the Rye"), so the title without it counts too, weighted by
    // the length of whatever actually matched — but only while it is still multi-word:
    // single words like "assignment" (The Assignment) or "giver" (The Giver) appear in
    // too many ordinary prompts.
    const titleWithoutArticle = title.replace(/^(the|a|an) /, '');
    const canMatchWithoutArticle = titleWithoutArticle.includes(' ');
    const getWeightForTitle = (matched) =>
      matched.length > 15 ? 60 : matched.length > 8 ? 40 : 20;

    // need to store this in a new variable because we're possibly going to modify it in a bit
    // and we want to keep the original topicCleaned for subsequent loops
    let revisedTopicCleaned = topicCleaned;

    let percentageSimilarity = 0;

    if (topicCleaned.includes(book.author.toLowerCase())) {
      percentageSimilarity += WEIGHT_FOR_AUTHOR;
      // remove the author from the input so that the levenshtein distance is more accurate
      revisedTopicCleaned = revisedTopicCleaned.replace(book.author.toLowerCase(), '').trim();
    }
    if (revisedTopicCleaned.includes(title)) {
      percentageSimilarity += getWeightForTitle(title);
    } else if (canMatchWithoutArticle && revisedTopicCleaned.includes(titleWithoutArticle)) {
      percentageSimilarity += getWeightForTitle(titleWithoutArticle);
    }

    // "chapter" anywhere (incl. "chapter10-12"), or the abbreviation "ch 3" / "chs 10-12"
    // (punctuation is already stripped, so "Ch." arrives as "ch")
    if (/chapter|\bchs? ?\d/.test(revisedTopicCleaned)) {
      percentageSimilarity += WEIGHT_FOR_CHAPTER;
    }

    const levDist = levenshteinDistance(
      revisedTopicCleaned.replace(' ', ''),
      title.replace(' ', '')
    );
    percentageSimilarity +=
      (1 - levDist / Math.max(revisedTopicCleaned.length, title.length)) * 100;

    if (percentageSimilarity > SCORE_TO_MATCH_ON) {
      matches.push({ book, percentageSimilarity });
    }
  }
  // sort the matches by percentage similarity
  matches.sort((a, b) => b.percentageSimilarity - a.percentageSimilarity);

  // remove the percentage similarity from the return value
  return matches.map((match) => match.book);
};

const getEdgeCaseMatches = (topic, books) => {
  if (topic.includes('romeo and juliet')) {
    const realTitle = 'The Tragedy of Romeo and Juliet';
    return books.filter((book) => book.title === realTitle);
  }
  if (topic.includes('macbeth')) {
    const realTitle = 'Macbeth';
    return books.filter((book) => book.title === realTitle);
  }
  if (topic.includes('the odyssey')) {
    const realTitle = 'Homer_The Odyssey';
    return books.filter((book) => book.title === realTitle);
  }
  if (topic.includes('treasure island')) {
    const realTitle = 'Robert Louis Stevenson_Treasure Island';
    return books.filter((book) => book.title === realTitle);
  }
  return false;
};
